/**
 * Datetime utility functions for locale-aware formatting and timezone handling
 */

/**
 * Get the current browser timezone
 */
export function getCurrentTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get the current browser locale
 */
export function getCurrentLocale(): string {
  return Intl.DateTimeFormat().resolvedOptions().locale;
}

/**
 * Format a datetime string to locale-aware format with timezone
 */
export function formatDateTime(date: string, timezone?: string): string {
  const dateObj = new Date(date);
  const tz = timezone || getCurrentTimezone();
  
  return new Intl.DateTimeFormat('en-GB', { // Force DD/MM/YYYY format
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz,
    timeZoneName: 'short'
  }).format(dateObj);
}

/**
 * Format a date string to DD/MM/YYYY format
 */
export function formatDate(date: string, timezone?: string): string {
  const dateObj = new Date(date);
  const tz = timezone || getCurrentTimezone();
  
  return new Intl.DateTimeFormat('en-GB', { // Force DD/MM/YYYY format
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: tz
  }).format(dateObj);
}

/**
 * Convert a local datetime string to ISO 8601 with timezone
 */
export function toISOWithTimezone(dateTimeLocal: string, timezone?: string): string {
  const tz = timezone || getCurrentTimezone();
  
  // Create a date object from the local datetime string
  const date = new Date(dateTimeLocal);
  
  // Format to ISO string with timezone
  return date.toLocaleString('sv-SE', { timeZone: tz }).replace(' ', 'T') + 'Z';
}

/**
 * Convert ISO datetime to local datetime string for input fields
 */
export function parseToLocal(isoDate: string, timezone?: string): string {
  const date = new Date(isoDate);
  
  // Convert to local datetime string format (YYYY-MM-DDTHH:mm)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Get timezone display name (e.g., "Europe/Amsterdam", "America/New_York")
 */
export function getTimezoneDisplayName(timezone?: string): string {
  return timezone || getCurrentTimezone();
}

/**
 * Detect date format preference from browser locale
 */
export function detectDateFormat(): 'DD/MM/YYYY' | 'MM/DD/YYYY' {
  const testDate = new Date(2023, 11, 25); // December 25, 2023
  const formatted = new Intl.DateTimeFormat().format(testDate);
  
  // Check if day comes before month (DD/MM/YYYY)
  if (formatted.startsWith('25')) {
    return 'DD/MM/YYYY';
  }
  return 'MM/DD/YYYY';
}

/**
 * Detect 12h/24h time format preference from browser locale
 */
export function detect12Hour(): boolean {
  // Check the browser's default hour12 setting
  const options = new Intl.DateTimeFormat().resolvedOptions();
  return options.hour12 === true;
}
